'use client';

import { useEffect } from 'react';
import { AlertTriangle, Home, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';

/**
 * Root Error Page
 *
 * Next.js error page that handles errors in route segments.
 * This file automatically wraps a route segment and its nested children
 * in a React Error Boundary.
 *
 * Features:
 * - Catches errors in nested route segments
 * - Displays user-friendly error messages
 * - Shows detailed error info in development
 * - Provides reset and navigation options
 * - Dark theme consistent with app design
 *
 * @see https://nextjs.org/docs/app/api-reference/file-conventions/error
 */
export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    // Log error to console for debugging
    console.error('Error page caught an error:', error);
  }, [error]);

  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center px-4 py-8">
      <div className="max-w-2xl w-full">
        <div className="bg-gray-800 rounded-xl border border-gray-700 p-8 shadow-2xl">
          {/* Error Icon and Title */}
          <div className="flex items-start gap-4 mb-6">
            <div className="flex-shrink-0 w-12 h-12 bg-red-500/10 rounded-full flex items-center justify-center">
              <AlertTriangle className="w-6 h-6 text-red-500" />
            </div>
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-white mb-2">
                Oops! Something went wrong
              </h1>
              <p className="text-gray-400">
                We encountered an unexpected error while processing your request.
              </p>
            </div>
          </div>

          {/* Error Details (Development Only) */}
          {process.env.NODE_ENV === 'development' && (
            <div className="mb-6">
              <div className="bg-gray-900 rounded-lg p-4 border border-gray-700">
                <h2 className="text-sm font-semibold text-red-400 mb-3">
                  Development Error Details
                </h2>

                {/* Error Message */}
                <div className="mb-3">
                  <p className="text-xs text-gray-500 mb-1">Message:</p>
                  <p className="text-sm text-gray-300 font-mono break-words">
                    {error.message || 'No error message available'}
                  </p>
                </div>

                {/* Error Digest (if available) */}
                {error.digest && (
                  <div className="mb-3">
                    <p className="text-xs text-gray-500 mb-1">Error Digest:</p>
                    <p className="text-sm text-gray-400 font-mono">
                      {error.digest}
                    </p>
                  </div>
                )}

                {/* Stack Trace */}
                {error.stack && (
                  <details className="mt-3">
                    <summary className="text-sm text-gray-400 cursor-pointer hover:text-gray-300 select-none">
                      View Stack Trace
                    </summary>
                    <pre className="mt-2 text-xs text-gray-500 overflow-x-auto p-3 bg-gray-950 rounded border border-gray-800">
                      {error.stack}
                    </pre>
                  </details>
                )}
              </div>
            </div>
          )}

          {/* Production Error Message */}
          {process.env.NODE_ENV === 'production' && (
            <div className="mb-6 bg-gray-900 rounded-lg p-4 border border-gray-700">
              <p className="text-sm text-gray-400">
                An error occurred while loading this page. Our team has been notified
                and we&apos;re working to fix the issue. Please try again in a few moments.
              </p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              onClick={reset}
              variant="primary"
              className="flex-1 flex items-center justify-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Try Again
            </Button>
            <Button
              onClick={() => window.location.href = '/'}
              variant="secondary"
              className="flex-1 flex items-center justify-center gap-2"
            >
              <Home className="w-4 h-4" />
              Go Home
            </Button>
          </div>
        </div>

        {/* Additional Help */}
        <div className="mt-6 text-center">
          <p className="text-sm text-gray-500">
            If this problem persists, please{' '}
            <a
              href="https://github.com/yourusername/vibeatlas/issues"
              className="text-blue-400 hover:text-blue-300 underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              report an issue
            </a>
            {' '}or contact support.
          </p>
        </div>
      </div>
    </div>
  );
}
